import { useTranslation } from 'react-i18next';
import { ListToolbar } from '@/components/list/ListToolbar';
import type { FilterField } from '@/components/list/filters';
import type { usePurposeQuery } from './use-purpose-query';
type View = ReturnType<typeof usePurposeQuery>;

/** Shared list controls; the purpose views still supply their implicit URL scope. */
export function PurposeToolbar({ view }: { readonly view: View }) {
  const { t } = useTranslation('expenses');
  const fields = usePurposeFilterFields();
  const options = (
    ['code', 'label', 'noteTypes', 'active', 'restricted', 'updatedAt'] as const
  ).flatMap((column) =>
    (['asc', 'desc'] as const).map((direction) => ({
      value: `${column}:${direction}`,
      label: `${t(`configuration.columns.${column}`)} · ${t(`configuration.${direction}`)}`,
    })),
  );
  return (
    <ListToolbar
      label={t('configuration.title')}
      search={{
        label: t('configuration.search'),
        value: view.query.search ?? '',
        onWrite: (value) => view.change('q', value),
      }}
      fields={fields}
      // Only what the person chose: a view's own scope («Attive», «Riservate») is already its
      // tab, and a chip saying the same thing is noise (M21).
      active={{
        active: view.params.get('active'),
        restricted: view.params.get('restricted'),
        noteType: view.query.noteType ?? null,
      }}
      onFilter={view.change}
      onClear={view.clear}
      sort={{
        label: t('configuration.sort'),
        value: `${view.query.sort}:${view.query.direction}`,
        options,
        onChange: (value) => view.sortBy(value),
      }}
      pageSize={{
        value: view.query.pageSize ?? 50,
        onChange: (size) => view.change('pageSize', String(size)),
      }}
      views={{
        label: t('configuration.views'),
        items: (['active', 'archived', 'reserved', 'all'] as const).map((id) => ({
          id,
          label: t(`configuration.viewsLabels.${id}`),
          params: { view: id },
        })),
        current: { get: (key) => (key === 'view' ? view.view : null) },
        onSelect: (item) => view.selectView(item.id),
      }}
    />
  );
}

function usePurposeFilterFields() {
  const { t } = useTranslation('expenses');
  const fields: FilterField[] = [
    {
      key: 'active',
      label: t('configuration.availability'),
      options: [
        { value: 'true', label: t('configuration.active') },
        { value: 'false', label: t('configuration.archived') },
      ],
    },
    {
      key: 'restricted',
      label: t('configuration.access'),
      options: [
        { value: 'true', label: t('configuration.restricted') },
        { value: 'false', label: t('configuration.open') },
      ],
    },
    {
      key: 'noteType',
      label: t('configuration.noteType'),
      options: (['travel', 'reimbursement'] as const).map((value) => ({
        value,
        label: t(`reports.noteType.${value}`),
      })),
    },
  ];
  return fields;
}
